import { Injectable } from '@angular/core';
import { KeycloakProfile } from 'keycloak-js';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '@app-environment';
import { getMappedRoleForUser } from '../config/dashboard-config/role-map/role-map';
import { RoleMenu } from '../config/dashboard-config/role-map/user-role';

interface AccessState {
  email: string | null;
  username: string | null;
  role: string | null;
  allowedRoutes: string[];
}

export function normalizeAccessRoute(url: string): string {
  const withoutQueryOrFragment = String(url || '').split(/[?#]/, 1)[0];
  const path = withoutQueryOrFragment.trim().toLocaleLowerCase().replace(/^\/+|\/+$/g, '');
  return path ? `/${path}` : '/';
}

@Injectable({ providedIn: 'root' })
export class AccessService {
  private accessState: AccessState | null = null;
  private accessPromise: Promise<AccessState> | null = null;

  constructor(private readonly keycloak: KeycloakService) {}

  async initialize(): Promise<boolean> {
    try {
      const authenticated = await this.keycloak.init({
        config: {
          url: environment.KEYCLOAK_URL,
          realm: environment.KEYCLOAK_REALM,
          clientId: environment.KEYCLOAK_CLIENT_ID,
        },
        initOptions: {
          onLoad: 'check-sso',
          checkLoginIframe: false,
          silentCheckSsoRedirectUri: `${window.location.origin}/assets/silent-check-sso.html`,
          pkceMethod: 'S256',
        },
        // Authorization is added by the functional interceptor after refresh.
        enableBearerInterceptor: false,
      });

      if (authenticated) await this.resolveAccess();
      else this.clearAccessCache();
      return authenticated;
    } catch (error) {
      this.clearAccessCache();
      throw error;
    }
  }

  isLoggedIn(): boolean {
    return this.keycloak.isLoggedIn();
  }

  async login(returnUrl: string): Promise<void> {
    const safePath = this.safeLocalPath(returnUrl);
    await this.keycloak.login({ redirectUri: `${window.location.origin}${safePath}` });
  }

  async logout(): Promise<void> {
    this.clearAccessCache();
    await this.keycloak.logout(window.location.origin);
  }

  async getUserEmail(): Promise<string | null> {
    return (await this.resolveAccess()).email;
  }

  async getUserRole(): Promise<string | null> {
    return (await this.resolveAccess()).role;
  }

  async getAllowedRoutes(): Promise<string[]> {
    return [...(await this.resolveAccess()).allowedRoutes];
  }

  async canAccessRoute(url: string): Promise<boolean> {
    if (normalizeAccessRoute(url) === '/access-denied') return true;
    const requested = normalizeAccessRoute(url);
    return (await this.resolveAccess()).allowedRoutes.includes(requested);
  }

  async getFirstAllowedRoute(): Promise<string | null> {
    return (await this.resolveAccess()).allowedRoutes[0] ?? null;
  }

  async getValidToken(minValiditySeconds = 30): Promise<string | null> {
    if (!this.isLoggedIn()) return null;
    try {
      await this.keycloak.updateToken(minValiditySeconds);
      return this.keycloak.getKeycloakInstance().token ?? null;
    } catch {
      this.clearAccessCache();
      return null;
    }
  }

  clearAccessCache(): void {
    this.accessState = null;
    this.accessPromise = null;
  }

  private resolveAccess(): Promise<AccessState> {
    if (this.accessState) return Promise.resolve(this.accessState);
    if (this.accessPromise) return this.accessPromise;

    this.accessPromise = this.buildAccessState()
      .then((state) => (this.accessState = state))
      .catch((error) => {
        this.clearAccessCache();
        throw error;
      })
      .finally(() => {
        this.accessPromise = null;
      });
    return this.accessPromise;
  }

  private async buildAccessState(): Promise<AccessState> {
    if (!this.isLoggedIn()) return this.emptyAccessState();

    const claims = this.keycloak.getKeycloakInstance().tokenParsed as Record<string, unknown> | undefined;
    let profile: KeycloakProfile | null = null;
    const claimEmail = this.stringClaim(claims?.['email']);
    if (!claimEmail) {
      try {
        profile = await this.keycloak.loadUserProfile();
      } catch {
        profile = null;
      }
    }

    const email = claimEmail || this.stringClaim(profile?.email) || null;
    const username = this.stringClaim(claims?.['preferred_username'])
      || this.stringClaim(claims?.['upn'])
      || this.stringClaim(profile?.username)
      || null;
    const role = getMappedRoleForUser(email, username);
    const allowedRoutes = role
      ? (RoleMenu[role] ?? []).map(normalizeAccessRoute).filter((route, index, all) => all.indexOf(route) === index)
      : [];

    return { email, username, role, allowedRoutes };
  }

  private emptyAccessState(): AccessState {
    return { email: null, username: null, role: null, allowedRoutes: [] };
  }

  private stringClaim(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private safeLocalPath(value: string): string {
    const candidate = String(value || '').trim();
    return candidate.startsWith('/') && !candidate.startsWith('//')
      ? candidate
      : '/';
  }
}
